import { createSlice } from '@reduxjs/toolkit';
import {
  createSlot,
  deleteSlot,
  fetchSlotOptions,
  fetchSlots,
  updateSlot,
  uploadSlots,
} from './slotsThunks';
import { ALL_ZONES } from './slotsConstants';
import { selectWarehouse } from '../warehouseContext/warehouseContextSlice';

const initialState = {
  /** Every location that matches the filters; the table pages them itself. */
  slots: [],
  loading: false,
  error: null,
  /** The request whose answer may still write. See `fetchSlots.fulfilled`. */
  requestId: null,
  filters: {
    search: '',
    zoneType: ALL_ZONES,
    page: 1,
    rowsPerPage: 20,
  },

  /** The form is being saved. Its failure is shown inside the form, not here. */
  saving: false,
  /** A sheet is being loaded. Its failure is shown inside the dialog. */
  uploading: false,
  /** The row being switched on or off, or deleted, to disable only its controls. */
  busyId: null,

  /** Active locations of `optionsWarehouseId`, for pickers rather than the table. */
  options: [],
  optionsWarehouseId: null,
  loadingOptions: false,
};

const slotsSlice = createSlice({
  name: 'slots',
  initialState,
  reducers: {
    /** Merges a patch into the filters; anything but a page change goes back to page 1. */
    setSlotFilters: (state, action) => {
      state.filters = { ...state.filters, page: 1, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSlots.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.requestId = action.meta.requestId;
      })
      .addCase(fetchSlots.fulfilled, (state, action) => {
        // Only the latest request may write: a write reloads the listing while
        // a filter may still be on its way.
        if (action.meta.requestId !== state.requestId) return;
        state.loading = false;
        state.slots = action.payload;
      })
      .addCase(fetchSlots.rejected, (state, action) => {
        if (action.meta.requestId !== state.requestId) return;
        state.loading = false;
        state.error = action.payload;
        state.slots = [];
      })

      .addCase(fetchSlotOptions.pending, (state, action) => {
        state.loadingOptions = true;
        // Locations of another warehouse must not be pickable while these load.
        if (state.optionsWarehouseId !== action.meta.arg) state.options = [];
        state.optionsWarehouseId = action.meta.arg;
      })
      .addCase(fetchSlotOptions.fulfilled, (state, action) => {
        if (action.payload.warehouseId !== state.optionsWarehouseId) return;
        state.loadingOptions = false;
        state.options = action.payload.slots;
      })
      .addCase(fetchSlotOptions.rejected, (state) => {
        state.loadingOptions = false;
        state.options = [];
      })

      .addCase(createSlot.pending, (state) => {
        state.saving = true;
      })
      .addCase(createSlot.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(createSlot.rejected, (state) => {
        state.saving = false;
      })

      .addCase(uploadSlots.pending, (state) => {
        state.uploading = true;
      })
      .addCase(uploadSlots.fulfilled, (state) => {
        state.uploading = false;
      })
      .addCase(uploadSlots.rejected, (state) => {
        state.uploading = false;
      })

      // An edit comes from the form; a switch of `is_active` comes from the
      // row. Both are the same call, so both flags follow it.
      .addCase(updateSlot.pending, (state, action) => {
        state.saving = true;
        state.busyId = action.meta.arg.slotId;
      })
      .addCase(updateSlot.fulfilled, (state) => {
        state.saving = false;
        state.busyId = null;
      })
      .addCase(updateSlot.rejected, (state) => {
        state.saving = false;
        state.busyId = null;
      })

      .addCase(deleteSlot.pending, (state, action) => {
        state.busyId = action.meta.arg;
      })
      .addCase(deleteSlot.fulfilled, (state) => {
        state.busyId = null;
      })
      .addCase(deleteSlot.rejected, (state) => {
        state.busyId = null;
      })

      // Another warehouse is another rack: page 3 of this one may not exist.
      .addCase(selectWarehouse, (state) => {
        state.filters.page = 1;
      });
  },
});

export const { setSlotFilters } = slotsSlice.actions;

export default slotsSlice.reducer;
